import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation, useRouteMatch } from 'quartz-routing/react-router';
import { DEFAULT_FILTER_VALUES, DEFAULT_SORT_VALUE, TicketListingView } from '../constants/filters';
import { ROUTES } from '../constants/routes';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_STATE = {
  view: TicketListingView.TABLE,
  sort: DEFAULT_SORT_VALUE,
  offset: 0,
  createDateFilter: DEFAULT_FILTER_VALUES.createDateFilter,
  lastActivityFilter: DEFAULT_FILTER_VALUES.lastActivityFilter,
  statusFilter: DEFAULT_FILTER_VALUES.statusFilter,
  ticketView: DEFAULT_FILTER_VALUES.ticketView,
  searchString: ''
};
function readFromUrl(search) {
  var _ref, _params$get, _ref2, _ref3, _ref4, _ref5, _params$get2;
  const params = new URLSearchParams(search);
  const sortRaw = params.get('sort');
  let sort = DEFAULT_STATE.sort;
  try {
    if (sortRaw) sort = JSON.parse(sortRaw);
  } catch (_unused) {
    // ignore malformed sort param
  }
  return {
    view: (_ref = params.get('view')) !== null && _ref !== void 0 ? _ref : DEFAULT_STATE.view,
    sort,
    offset: Number((_params$get = params.get('offset')) !== null && _params$get !== void 0 ? _params$get : 0),
    createDateFilter: (_ref2 = params.get('createdate')) !== null && _ref2 !== void 0 ? _ref2 : DEFAULT_STATE.createDateFilter,
    lastActivityFilter: (_ref3 = params.get('hs_lastactivitydate')) !== null && _ref3 !== void 0 ? _ref3 : DEFAULT_STATE.lastActivityFilter,
    statusFilter: (_ref4 = params.get('statusFilter')) !== null && _ref4 !== void 0 ? _ref4 : DEFAULT_STATE.statusFilter,
    ticketView: (_ref5 = params.get('ticketView')) !== null && _ref5 !== void 0 ? _ref5 : DEFAULT_STATE.ticketView,
    searchString: (_params$get2 = params.get('searchString')) !== null && _params$get2 !== void 0 ? _params$get2 : ''
  };
}
function setOrDelete(params, key, value, defaultValue) {
  if (value === defaultValue || !value && !defaultValue) {
    params.delete(key);
  } else {
    params.set(key, value);
  }
}
function writeStateToParams(params, state) {
  setOrDelete(params, 'view', state.view, DEFAULT_STATE.view);
  setOrDelete(params, 'sort', JSON.stringify(state.sort), JSON.stringify(DEFAULT_STATE.sort));
  setOrDelete(params, 'offset', `${state.offset}`, '0');
  setOrDelete(params, 'createdate', state.createDateFilter, DEFAULT_STATE.createDateFilter);
  setOrDelete(params, 'hs_lastactivitydate', state.lastActivityFilter, DEFAULT_STATE.lastActivityFilter);
  setOrDelete(params, 'statusFilter', state.statusFilter, DEFAULT_STATE.statusFilter);
  setOrDelete(params, 'ticketView', state.ticketView, DEFAULT_STATE.ticketView);
  setOrDelete(params, 'searchString', state.searchString, '');
}
const ListingsSortAndFiltersContext = /*#__PURE__*/createContext(null);
export function ListingsSortAndFiltersProvider({
  children
}) {
  const location = useLocation();
  const isOnListingsRoute = !!useRouteMatch({
    path: ROUTES.root,
    exact: true
  });
  const isOnTicketRoute = !!useRouteMatch({
    path: ROUTES.ticket
  });
  const [filters, setFiltersState] = useState(() => readFromUrl(location.search));
  useEffect(() => {
    if (!isOnListingsRoute && !isOnTicketRoute) return;
    const params = new URLSearchParams();
    writeStateToParams(params, filters);
    const search = params.toString();
    const url = search ? `?${search}` : window.location.pathname;
    //TODO: Consider swapping for history.replace
    window.history.replaceState(null, '', url);
  }, [location.pathname, isOnListingsRoute, isOnTicketRoute, filters]);
  const contextValue = useMemo(() => Object.assign({}, filters, {
    setFiltersState
  }), [filters, setFiltersState]);
  return /*#__PURE__*/_jsx(ListingsSortAndFiltersContext.Provider, {
    value: contextValue,
    children: children
  });
}
export function useListingsSortAndFiltersContext() {
  const context = useContext(ListingsSortAndFiltersContext);
  if (!context) {
    throw new Error('useListingsSortAndFiltersContext must be used within a ListingsSortAndFiltersProvider');
  }
  return context;
}